import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { connect } from 'react-redux';
import { approveLeadVaultAccess, unblockLeadVaultAccess } from 'bundles/project/actions/index';
import Snackbar from 'shared/snackbar';
import { LeadBanner } from './lead_banner';
import { isEmpty } from 'lodash';

function parseFormattedDate(lastFollowUpSentAt) {
  const rawDate = new Date(lastFollowUpSentAt);
  const displayDate = rawDate.toLocaleDateString('en-US', { year: '2-digit', month: '2-digit', day: '2-digit' });
  const displayTime = rawDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return lastFollowUpSentAt != null ? `Last sent on ${displayDate} at ${displayTime}` : '';
}

function NotifyLead({
  approveLeadVaultAccess,
  lead,
  leadInfo,
  unblockLeadVaultAccess
}) {
  const { approvalStatus, caRequired, enabledVaultGates, propertyVaultProfiles, projectId } = lead;
  const { accountNotVerified, caNotSigned, pendingApproval, profileNotComplete, viewDocsPrompt } = leadInfo;

  const [error, setError] = useState(null);
  const [lastFollowUpSentAt, setLastFollowUpSentAt] = useState(lead.lastFollowUpSentAt);
  const [showReminderSentSnackbar, setShowReminderSentSnackbar] = useState(false);

  const leadHasPvps = lead.propertyVaultProfiles.some(pvp => !!pvp.id);

  if (!leadHasPvps) return null;

  const leadBlocked = approvalStatus === 'blocked';
  const formattedDate = parseFormattedDate(lastFollowUpSentAt);

  const bannerContent = [
    {
      callToAction: {
        buttonText: 'Unblock',
        onClick: () => unblockLead(),
      },
      content: 'Would you like to unblock this lead?',
      show: leadBlocked,
      title: 'This lead is blocked',
      variant: 'danger',
    },
    {
      callToAction: {
        buttonText: 'Send Reminder',
        onClick: () => sendProfileNotification(),
        detailsText: formattedDate,
      },
      content: 'Would you like to remind them to complete their information?',
      show: enabledVaultGates ? enabledVaultGates['collectInformation'] && profileNotComplete : profileNotComplete,
      title: 'This lead has not completed their Information',
      variant: 'vgCollectInfo',
    },
    {
      callToAction: {
        buttonText: 'Send Reminder',
        onClick: () => sendAccountNotification(),
        detailsText: formattedDate,
      },
      content: 'Would you like to remind them to verify their email?',
      show: enabledVaultGates ? enabledVaultGates['verification'] && accountNotVerified : accountNotVerified,
      title: 'This lead hasn\'t verified their email',
      variant: 'vgVerification',
    },
    {
      callToAction: {
        buttonText: 'Send Reminder',
        onClick: () => sendCaNotification(),
        detailsText: formattedDate,
      },
      content: 'Would you like to remind them to sign the CA?',
      show: enabledVaultGates ? enabledVaultGates['signCa'] && caNotSigned : (caRequired && caNotSigned),
      title: 'This lead has not signed the CA',
      variant: 'vgSignCa',
    },
    {
      important: true, // always show if `show` is true
      callToAction: {
        buttonText: 'Approve Lead',
        onClick: () => approveLead(),
      },
      content: 'Would you like to approve this lead to give them access?',
      show: enabledVaultGates
        ? enabledVaultGates['waitlist'] && pendingApproval && !leadBlocked
        : pendingApproval && !leadBlocked,
      title: 'This lead has been waitlisted',
      variant: 'vgWaitlist',
    },
    {
      callToAction: {
        buttonText: 'Send Reminder',
        onClick: () => sendViewDocumentsReminder(),
      },
      content: 'Would you like to remind them to view the documents?',
      show: viewDocsPrompt,
      title: 'This lead has not viewed your locked documents yet',
      variant: 'info',
    },
  ];

  const approveLead = () => {
    propertyVaultProfiles.forEach((pvp) => {
      if (pvp.approveAccessPath) approveLeadVaultAccess(lead.id, pvp.id, pvp.approveAccessPath);
    });
  };

  const unblockLead = () => {
    unblockLeadVaultAccess(lead.id, projectId);
  };

  const sendProfileNotification = () => {
    sendNotification(propertyVaultProfiles[0].followUpPath, 'profile_not_complete');
  };

  const sendViewDocumentsReminder = () => {
    sendNotification(propertyVaultProfiles[0].followUpPath, 'view_documents_reminder');
  };

  const sendAccountNotification = () => {
    if (propertyVaultProfiles[0].verified === false) {
      sendNotification(propertyVaultProfiles[0].followUpPath, 'account_not_verified');
    } else if (propertyVaultProfiles[1]?.verified === false) {
      sendNotification(propertyVaultProfiles[1].followUpPath, 'account_not_verified');
    }
  };

  const sendCaNotification = () => {
    if (isEmpty(propertyVaultProfiles[0].caSignatureUrl)) {
      sendNotification(propertyVaultProfiles[0].followUpPath, 'ca_not_signed');
    } else if (propertyVaultProfiles.length > 1) {
      sendNotification(propertyVaultProfiles[1].followUpPath, 'ca_not_signed');
    }
  };

  const sendNotification = (url, type) => {
    axios.post(url, { type })
      .then((response) => {
        setLastFollowUpSentAt(response.data.lastFollowUpSentAt);

        setShowReminderSentSnackbar(true);
      })
      .catch((error) => {
        setError(error);
        setTimeout(() => {
          setError(null);
        }, 5000);
      });
  };

  // include the first banner and all important ones
  const bannersToShow =
    bannerContent
      .filter(item => item.show)
      .filter((item, idx) => {
        if (item.important || idx === 0) return item;
      });


  return (
    <React.Fragment>
      <div className="d-flex flex-column gap-3" style={{ marginBottom: bannersToShow.length > 0 ? '32px' : '0' }}>
        {error && <LeadBanner content={error} title="Error While Sending Notification" variant="danger"/>}
        {bannersToShow.map((item) => {
          return (
            <LeadBanner
              callToAction={item.callToAction}
              content={item.content}
              key={item.title}
              show={item.show}
              title={item.title}
              variant={item.variant}
            />
          );
        })}
      </div>

      <Snackbar
        show={showReminderSentSnackbar}
        text="Reminder Sent!"
        variant="success"
        onClose={() => setShowReminderSentSnackbar(false)}
      />
    </React.Fragment>
  );
}

NotifyLead.propTypes = {
  approveLeadVaultAccess: PropTypes.func.isRequired,
  lead: PropTypes.shape({
    approvalStatus: PropTypes.string,
    caRequired: PropTypes.bool.isRequired,
    enabledVaultGates: PropTypes.object,
    propertyVaultProfiles: PropTypes.array
  }).isRequired,
  leadInfo: PropTypes.shape({
    accountNotVerified: PropTypes.bool.isRequired,
    caNotSigned: PropTypes.bool.isRequired,
    pendingApproval: PropTypes.bool.isRequired,
    profileNotComplete: PropTypes.bool.isRequired,
    viewDocsPrompt: PropTypes.bool.isRequired,
  }).isRequired,
  unblockLeadVaultAccess: PropTypes.func.isRequired
};

const actions = {
  approveLeadVaultAccess,
  unblockLeadVaultAccess
};

export default connect(null, actions)(NotifyLead);
